<template>
    <ob1-search-field
        id="search-field"
        v-model="value"
        :label="placeHolder"
        :show-auto-completion="showAutoCompletion"
        :tms-button-category="tmsCategory"
        :tms-button-action="tmsButtonAction"
        :tms-button-label="computeTmsLabel()"
        :tms-result-category="tmsCategory"
        :tms-result-action="tmsResultAction"
        :tms-result-label="computeTmsLabel()"
        @input="inputSearchFiledHandler"
    />
</template>

<script>
import {algoliasearch} from "algoliasearch";

export default {
    props: {
        placeHolder: {
            type: String,
            default: ""
        },
        searchResultsUrl: {
            type: String,
            required: true
        },
        searchResultsQueryAdditionalParams: {
            type: String,
            required: true
        },
        algoliaAppId: {
            type: String,
            required: true
        },
        algoliaApiKey: {
            type: String,
            required: true
        },
        algoliaIndexName: {
            type: String,
            required: true
        },
        tmsCategory: {
            type: String,
            default: null
        },
        tmsButtonAction: {
            type: String,
            default: null
        },
        tmsResultAction: {
            type: String,
            default: null
        },
        enableSearchAlgolia: {
            type: Boolean,
            default: false
        },
        deviceListSearchUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            value: "",
            responseList: [""], // astuce pour ne pas afficher d'erreur avant la saisie de 3 caractères
            showAutoCompletion: false,
            searchRequestId: 0,
            isSearchPending: false,
            isSearchDestroyed: false,
            selectionKey: null,
            resultsObserver: null
        }
    },
    mounted: function () {
        this.searchFieldHandler();
        this.prefillFromUrl();
        document.addEventListener('mousedown', this.closeOnClickOutside);
        this.$el.addEventListener('focusout', this.closeOnFocusOutside);
        this.$el.addEventListener('keydown', this.closeOnEscape, true);
        this.$el.addEventListener('keydown', this.prepareSelection, true);
        this.$el.addEventListener('click', this.prepareSelection, true);
        this.$el.addEventListener('keydown', this.closeOnSelection);
        this.$el.addEventListener('click', this.closeOnSelection);
        this.$el.addEventListener('keyup', this.preventHiddenResults, true);
        this.$el.addEventListener('search', this.preventHiddenResults, true);
        this.$el.addEventListener('keyup', this.onSearchKeyup);
        this.observeResultsAccessibility();
        this.syncResultsAccessibility();
    },
    beforeDestroy() {
        this.isSearchDestroyed = true;
        this.resultsObserver.disconnect();
        this.closeResultsList();
        document.removeEventListener('mousedown', this.closeOnClickOutside);
        this.$el.removeEventListener('focusout', this.closeOnFocusOutside);
        this.$el.removeEventListener('keydown', this.closeOnEscape, true);
        this.$el.removeEventListener('keydown', this.prepareSelection, true);
        this.$el.removeEventListener('click', this.prepareSelection, true);
        this.$el.removeEventListener('keydown', this.closeOnSelection);
        this.$el.removeEventListener('click', this.closeOnSelection);
        this.$el.removeEventListener('keyup', this.preventHiddenResults, true);
        this.$el.removeEventListener('search', this.preventHiddenResults, true);
        this.$el.removeEventListener('keyup', this.onSearchKeyup);
        this.$el.querySelector('#search-field-button').removeEventListener('click', this.launchSearch);
        this.$el.querySelector('#search-field').removeEventListener('keydown', this.ensureOb1Repository);
    },
    methods: {
        computeTmsLabel() {
            return this.tmsCategory ? this.value.trim().replace(/\s+/g, "") : null;
        },
        searchFieldHandler() {
            const searchFieldButton = this.$el.querySelector('#search-field-button')
            const searchField = this.$el.querySelector('#search-field')
            searchFieldButton.addEventListener('click', this.launchSearch);
            searchField.addEventListener('keydown', this.ensureOb1Repository);
        },
        ensureOb1Repository(event) {
            const searchField = ob1.Ob1Component.get(event.target)?.SearchField;
            if (searchField && searchField.repository.length === 0) {
                searchField.repository = this.responseList.length ? this.responseList : [""];
            }
        },
        isResultsListOpen() {
            const results = this.$el.querySelector('#search-field-results');
            return this.showAutoCompletion && results.children.length > 0
                && !results.classList.contains('d-none') && results.style.display !== 'none';
        },
        syncResultsAccessibility() {
            const input = this.$el.querySelector('#search-field');
            if (!this.showAutoCompletion && !this.$el.querySelector('#search-field-results').classList.contains('d-none')) {
                ob1.Ob1Component.get(input)?.SearchField?.displayResultsList(false);
            }
            if (input.getAttribute('aria-controls') !== 'search-field-results') {
                input.setAttribute('aria-controls', 'search-field-results');
            }
            const expanded = String(this.isResultsListOpen());
            if (input.getAttribute('aria-expanded') !== expanded) {
                input.setAttribute('aria-expanded', expanded);
            }
        },
        observeResultsAccessibility() {
            // ob1 réécrit l'ARIA au keyup et ne la remet pas à false quand il masque la liste.
            this.resultsObserver = new MutationObserver(this.syncResultsAccessibility);
            this.resultsObserver.observe(this.$el.querySelector('#search-field'), {
                attributes: true,
                attributeFilter: ['aria-expanded', 'aria-controls']
            });
            this.resultsObserver.observe(this.$el.querySelector('#search-field-results'), {
                childList: true,
                attributes: true,
                attributeFilter: ['class', 'style']
            });
        },
        closeResultsList() {
            // Invalider aussi les requêtes en attente quand ob1 masque momentanément la liste.
            this.searchRequestId++;
            this.isSearchPending = false;
            this.showAutoCompletion = false;
            this.responseList = [""];
            const input = this.$el.querySelector('#search-field');
            const searchField = ob1.Ob1Component.get(input)?.SearchField;
            if (searchField) {
                searchField.repository = this.responseList;
                searchField.selectedResult = -1;
                searchField.accessibility.textContent = '';
            }
            this.$el.querySelector('#search-field-results').innerHTML = '';
            this.syncResultsAccessibility();
        },
        closeOnClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.closeResultsList();
            }
        },
        closeOnFocusOutside(event) {
            if (event.relatedTarget) {
                if (!this.$el.contains(event.relatedTarget)) {
                    this.closeResultsList();
                }
                return;
            }
            this.$nextTick(() => {
                if (!this.isSearchDestroyed && !this.$el.contains(document.activeElement)) {
                    this.closeResultsList();
                }
            });
        },
        closeOnEscape(event) {
            if (event.key !== 'Escape' || (!this.showAutoCompletion && !this.isSearchPending)) {
                return;
            }
            // Le premier Échap ferme seulement les suggestions ; le suivant reste natif.
            event.preventDefault();
            event.stopPropagation();
            this.closeResultsList();
            this.$el.querySelector('#search-field').focus();
        },
        getSelectedSuggestion(event) {
            if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
                return null;
            }
            return event.target.closest('[role="option"]');
        },
        prepareSelection(event) {
            const suggestion = this.getSelectedSuggestion(event);
            if (!suggestion) {
                return;
            }
            // Synchroniser Vue avant qu'ob1 retire la suggestion focalisée et déclenche blur.
            this.value = suggestion.innerText;
            this.selectionKey = event.type === 'keydown' ? event.key : null;
            if (event.type === 'keydown') {
                event.preventDefault();
            }
        },
        closeOnSelection(event) {
            if (!this.getSelectedSuggestion(event)) {
                return;
            }
            if (event.type === 'keydown' && event.key === 'Enter') {
                this.launchSearch();
            } else {
                this.closeResultsList();
            }
            this.$el.querySelector('#search-field').focus();
        },
        preventHiddenResults(event) {
            if (this.selectionKey === event.key || (!this.showAutoCompletion && event.key !== 'Enter')) {
                event.stopPropagation();
                this.syncResultsAccessibility();
            }
            if (event.type === 'keyup') {
                this.selectionKey = null;
            }
        },
        onSearchKeyup(event) {
            if (event.key === 'Enter' && event.target.id === 'search-field') {
                this.launchSearch();
            }
            this.syncResultsAccessibility();
        },
        isCurrentSearch(requestId, value) {
            return !this.isSearchDestroyed && requestId === this.searchRequestId
                && value === this.value && value === this.$el.querySelector('#search-field').value
                && this.$el.contains(document.activeElement);
        },
        applySearchResults(results, searchField, input) {
            this.isSearchPending = false;
            const suggestions = results.results?.[0]?.hits.map(hit => hit.query).filter(query => query.trim().length > 0) || [];
            this.responseList = suggestions.length ? suggestions : [""];
            searchField.repository = this.responseList;
            this.showAutoCompletion = suggestions.length > 0;
            if (this.showAutoCompletion) {
                input.dispatchEvent(new Event("keyup", {bubbles: true}));
            }
            this.$nextTick(this.syncResultsAccessibility);
        },
        reportSearchError(error) {
            this.closeResultsList();
            if (window.MyShop && window.MyShop.crashLogReporter) {
                window.MyShop.crashLogReporter.logError(error);
            }
            console.error(error);
        },
        inputSearchFiledHandler(value) {
            this.closeResultsList();
            if (value.length < 3 || this.isSearchDestroyed) {
                return;
            }
            const elt = this.$el.querySelector("#search-field");
            const searchField = ob1.Ob1Component.get(elt)?.SearchField;
            if (searchField) {
                const requestId = this.searchRequestId;
                this.isSearchPending = true;
                const client = algoliasearch(this.algoliaAppId, this.algoliaApiKey);
                const indexName = this.algoliaIndexName;
                client.search({
                    requests: [
                        {
                            indexName: indexName,
                            query: value,
                            hitsPerPage: 5
                        },
                    ],
                }).then((results) => {
                        if (!this.isCurrentSearch(requestId, value)) {
                            return;
                        }
                        this.applySearchResults(results, searchField, elt);
                    }
                ).catch((error) => {
                    if (!this.isCurrentSearch(requestId, value)) {
                        return;
                    }
                    this.reportSearchError(error);
                })
            }
        },
        launchSearch() {
            this.closeResultsList();
            const searchField = document.getElementById('search-field');
            if (!searchField || !searchField.value.trim()) {
                return;
            }

            let rootUrl;
            if (this.enableSearchAlgolia) {
                rootUrl = this.deviceListSearchUrl;
            } else {
                rootUrl = this.searchResultsUrl;
            }

            this.value = searchField.value.trim();
            document.location.href = rootUrl + '?kw=' + encodeURIComponent(this.value) + this.searchResultsQueryAdditionalParams;
        },
        prefillFromUrl() {
            const term = new URLSearchParams(window.location.search).get('kw');
            if (!term) {
                return;
            }
            this.value = term;
            const searchField = document.getElementById('search-field');
            if (searchField) {
                searchField.value = term;
            }
        },
    }
}
</script>
